// Pestaña Prospectos de Clientes: filtros · tabla · ficha sobre la agenda completa
// (`GET /prospects?index=seller`), filtrada y paginada en memoria; catálogo y cartera solo enriquecen.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { MasterDetailExplorer } from '@/shared/patterns/MasterDetail';
import {
    ACTIVE_SESSION_LOOKUP_ENABLED,
    useActiveWizardSession,
    useCatalog,
    useMarkProspectLost,
    useProspects,
    useResumeProspect,
    useWizardSession,
} from '@/domains/sales/wizard/api/useWizard';
import { useAuth } from '@/hooks/useAuth';
import type { Permission } from '@/api/types/permissions';
import { useSellerContext } from '@/domains/sales/wizard/sellerContext';
import { lostReasonFor, prospectPhone, todayMx } from '@/domains/sales/wizard/prospects';
import { useCustomerDetail, useSellerCustomers } from './customers/api/useSellerCustomers';
import { useCreditPortfolio } from './customers/api/useCreditPortfolio';
import { useBorrowerRejections } from './customers/api/useBorrowerRejections';
import { customerCreditFactsOf } from './customers/lib/customerCreditFacts';
import { ProspectsFiltersPanel } from './prospects/components/ProspectsFiltersPanel';
import { ProspectsTable } from './prospects/components/ProspectsTable';
import { ProspectDetailPanel, type DetailTab } from './prospects/components/ProspectDetailPanel';
import {
    DEFAULT_SORT,
    EMPTY_FILTERS,
    countActiveFilters,
    filterProspects,
    prospectMetrics,
    sortProspects,
    type ProspectsFilters,
    type SortState,
} from './prospects/lib/prospectsFilters';
import { isSettled } from './prospects/lib/prospectsFormat';
import { REOPEN_CHECKPOINT_NOTE, reopenFailureOf } from './prospects/lib/prospectsReopen';
import { CANCEL_OTHER_STORE_PERMISSION } from '@/domains/sales/wizard/phoneHolderCancel';
import { CancelOtherStoreSaleDialog } from '@/domains/sales/wizard/components/CancelOtherStoreSaleDialog';
import { buildProspectRows } from './prospects/lib/prospectsRows';

/**
 * Lo que tarda en leerse con calma el aviso de «sin teléfono»: los 4 s por defecto de sonner no
 * alcanzan para sus dos renglones. Se acompaña de botón de cerrar para quien ya lo leyó.
 */
const NO_PHONE_TOAST_DURATION_MS = 12_000;

export function SalesProspects({
    prospectId,
    className,
}: {
    prospectId?: string;
    className?: string;
}) {
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const { sellerId } = useSellerContext();
    const [filters, setFilters] = useState<ProspectsFilters>(EMPTY_FILTERS);
    const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
    const [page, setPage] = useState(1);
    const [selectedId, setSelectedId] = useState<string | null>(prospectId ?? null);
    const [linkedId, setLinkedId] = useState(prospectId);
    const [tab, setTab] = useState<DetailTab>('prospecto');
    const [filtersCollapsed, setFiltersCollapsed] = useState(false);
    const [confirmingLost, setConfirmingLost] = useState(false);
    // Cerrada a mano, la ficha no cae a la primera fila: queda vacía hasta que elijan otra.
    const [detailClosed, setDetailClosed] = useState(false);
    // Teléfono cuya venta en otra sucursal el encargado va a cancelar.
    const [otherStorePhone, setOtherStorePhone] = useState<string | null>(null);

    // El deep-link `?prospect=` manda sobre lo elegido a mano: llegar por una URL nueva mueve la
    // ficha aunque el vendedor ya haya tocado otra fila.
    if (prospectId !== linkedId) {
        setLinkedId(prospectId);
        setSelectedId(prospectId ?? null);
        if (prospectId) setDetailClosed(false);
    }

    const list = useProspects(sellerId);
    const catalog = useCatalog('portal');
    const cartera = useSellerCustomers({});
    const markLost = useMarkProspectLost(sellerId);
    const resumeProspect = useResumeProspect(sellerId);

    // El día de hoy se calcula fuera del render (regla de pureza), en un callback del efecto.
    const [today, setToday] = useState('');
    useEffect(() => {
        const id = setTimeout(() => {
            setToday(todayMx());
        }, 0);
        return () => {
            clearTimeout(id);
        };
    }, []);

    // La cartera solo aporta el nombre del cliente identificado. Su error se ignora a propósito:
    // el rol `vendedor` puede no tener `retail.customer.list` y eso no puede tumbar la agenda.
    const customerNames = useMemo(
        () => new Map(cartera.rows.map((row) => [row.retailCustomerId, row.fullName])),
        [cartera.rows],
    );

    const rows = useMemo(
        () =>
            buildProspectRows(list.data?.items ?? [], {
                catalog: catalog.data?.items ?? [],
                customerNames,
                today,
            }),
        [list.data, catalog.data, customerNames, today],
    );

    // Sin lista leída, los conteos valen cero por ausencia de datos, no porque la agenda esté
    // vacía: la toolbar no puede afirmar un número al lado del panel que dice que la lectura falló.
    const hasProspectList = list.data !== undefined;

    const metrics = useMemo(() => prospectMetrics(rows), [rows]);

    const visibleRows = useMemo(
        () => sortProspects(filterProspects(rows, filters), sort),
        [rows, filters, sort],
    );

    /**
     * La fila abierta se DERIVA de lo que se ve, no se guarda aparte: el mockup abre con una fila ya
     * elegida, y si el filtro se lleva a la que estaba abierta hay que saltar a la primera para no
     * dejar una ficha ajena al resultado. Corregirlo con un efecto que escribe estado dispara
     * renders en cascada; calcularlo no.
     */
    const openedRow = useMemo(() => {
        if (detailClosed || visibleRows.length === 0) return undefined;
        return visibleRows.find((row) => row.folio === selectedId) ?? visibleRows[0];
    }, [visibleRows, selectedId, detailClosed]);

    const openedProspect = openedRow?.prospect;
    const customerDetail = useCustomerDetail(openedProspect?.retailCustomerId ?? null);
    // La ficha 360 en vuelo deja el teléfono en `null` sin que falte: «cargando» no es «no hay».
    const isCustomerLoading = openedProspect?.retailCustomerId != null && customerDetail.isPending;

    // El historial de crédito se lee SOLO del prospecto abierto, nunca por fila: el listado trae la
    // agenda entera y preguntarle a cada uno serían cientos de lecturas para una pestaña accesoria.
    const openedCustomerId = openedProspect?.retailCustomerId ?? null;
    // Clientes exige `retail.customer.search` (`salesNav`) y la ruta no la gatea: sin el permiso, el
    // salto llevaría a una tabla en 403.
    const canOpenCustomer = hasPermission('retail.customer.search' as Permission);
    const portfolioIds = useMemo(
        () => (openedCustomerId ? [openedCustomerId] : []),
        [openedCustomerId],
    );
    const portfolio = useCreditPortfolio(portfolioIds, list.isPending);
    // Sin detalle del crédito que ofrecer: esta pantalla solo lee la cartera, que es la que el
    // vendedor puede ver sin `loan.credit.read`.
    const creditFacts = customerCreditFactsOf({
        cell: openedCustomerId ? portfolio.byCustomer.get(openedCustomerId) : undefined,
        portfolioState: portfolio.state,
        creditEntry: null,
    });
    // El historial de evaluaciones exige `loan.scoring.read`, que el rol `vendedor` NO tiene: sin el
    // permiso ni se pide, y la fila dice que no se pudo leer en vez de afirmar un cero.
    const canReadScoring = hasPermission('loan.scoring.read' as Permission);
    const rejections = useBorrowerRejections({
        hasCustomer: openedCustomerId !== null,
        credit: creditFacts,
        canRead: canReadScoring,
        today,
    });

    // El prospecto identificado no captura teléfono: el suyo llega con la ficha del cliente, que es
    // best-effort. Sin ninguno de los dos no hay llave con la que buscar su venta.
    const wizardPhone = openedProspect
        ? (prospectPhone(openedProspect) ?? customerDetail.data?.customer.phone ?? null)
        : null;
    // El prospecto resuelto (CLOSED/LOST) no se consulta: su venta ya se cerró o se dio por perdida,
    // así que la respuesta sería `found: false` garantizado.
    const settledProspect = openedProspect ? isSettled(openedProspect.status) : false;
    const activeSession = useActiveWizardSession(
        wizardPhone,
        Boolean(openedProspect) && !settledProspect,
    );

    // Lee la venta POR ID donde no se puede preguntar por teléfono: la ficha resuelta, y la abierta sin
    // teléfono — solo esta última espera a la ficha 360, que todavía puede traer uno.
    const readsLinkedSession =
        openedProspect !== undefined && (settledProspect || (!isCustomerLoading && !wizardPhone));
    const linkedSessionId = readsLinkedSession ? openedProspect.wizardSessionId : null;
    const linkedSession = useWizardSession(linkedSessionId ?? '');

    const product = useMemo(
        () =>
            openedProspect?.productSku
                ? catalog.data?.items.find((item) => item.sku === openedProspect.productSku)
                : undefined,
        [catalog.data, openedProspect],
    );

    /** Elegir una fila la abre y la deja en `?tab=prospects&prospect=`, para recargar o compartir. */
    const selectRow = (prospectId: string) => {
        setSelectedId(prospectId);
        setDetailClosed(false);
        void navigate({
            to: '/sales/$section',
            params: { section: 'customers' },
            search: { tab: 'prospects', prospect: prospectId },
            replace: true,
        });
    };

    /** Cierra la ficha y saca el `?prospect=` de la URL, para que recargar no la vuelva a abrir. */
    const closeDetail = () => {
        setSelectedId(null);
        setDetailClosed(true);
        setTab('prospecto');
        void navigate({
            to: '/sales/$section',
            params: { section: 'customers' },
            search: { tab: 'prospects' },
            replace: true,
        });
    };

    // Cambiar los filtros re-arma la lista: seguir en la página 4 de un resultado de 1 página
    // mostraría una tabla vacía sin explicación.
    const applyFilters = (next: ProspectsFilters) => {
        setFilters(next);
        setPage(1);
    };

    /** «Reabrir» lo decide el silo: la venta viva del teléfono (misma sesión) o, sin ella, una nueva. */
    const reopen = async (): Promise<void> => {
        if (!openedProspect || resumeProspect.isPending) return;
        const prospect = openedProspect.prospectId;
        try {
            const res = await resumeProspect.mutateAsync(prospect);
            if (res.mode === 'NEW' || !res.sessionId) {
                await navigate({
                    to: '/wizard',
                    search: wizardPhone ? { phone: wizardPhone, prospect } : { prospect },
                });
                return;
            }
            if (res.mode === 'CHECKPOINT') toast.info(REOPEN_CHECKPOINT_NOTE);
            await navigate({ to: '/wizard/s/$sessionId', params: { sessionId: res.sessionId } });
        } catch (error) {
            const failure = reopenFailureOf(error);
            if (failure.kind === 'NO_PHONE') {
                toast.info(failure.message, {
                    duration: NO_PHONE_TOAST_DURATION_MS,
                    closeButton: true,
                });
                await navigate({ to: '/wizard', search: { prospect } });
                return;
            }
            if (
                failure.kind === 'OTHER_STORE' &&
                wizardPhone &&
                hasPermission(CANCEL_OTHER_STORE_PERMISSION as Permission)
            ) {
                const phone = wizardPhone;
                toast.error(failure.message, {
                    action: {
                        label: 'Cancelar esa venta',
                        onClick: () => {
                            setOtherStorePhone(phone);
                        },
                    },
                });
                return;
            }
            toast.error(failure.message);
            if (failure.kind === 'REFRESH_LIST' || failure.kind === 'CLOSED') void list.refetch();
        }
    };

    // Salta a Clientes con la ficha del cliente ya abierta.
    const openCustomer = (): void => {
        if (!openedCustomerId) return;
        void navigate({
            to: '/sales/$section',
            params: { section: 'customers' },
            search: { customer: openedCustomerId },
        });
    };

    const markLostConfirmed = async (): Promise<void> => {
        if (!openedProspect) return;
        try {
            await markLost.mutateAsync({
                prospectId: openedProspect.prospectId,
                reason: lostReasonFor(openedProspect),
            });
        } catch {
            toast.error('No se pudo marcar el prospecto como perdido. Intenta de nuevo.');
            return;
        }
        setConfirmingLost(false);
        toast.info('Prospecto marcado como perdido');
    };

    return (
        <>
            <MasterDetailExplorer
                className={className}
                filtersCollapsed={filtersCollapsed}
                filters={
                    <ProspectsFiltersPanel
                        filters={filters}
                        onChange={applyFilters}
                        rows={rows}
                        collapsed={filtersCollapsed}
                        onToggleCollapsed={() => {
                            setFiltersCollapsed((v) => !v);
                        }}
                    />
                }
                list={
                    <ProspectsTable
                        rows={visibleRows}
                        metrics={metrics}
                        hasList={hasProspectList}
                        today={today}
                        page={page}
                        onPageChange={setPage}
                        sort={sort}
                        onSortChange={setSort}
                        selectedId={openedRow?.folio ?? null}
                        onSelect={selectRow}
                        isLoading={list.isPending}
                        isTruncated={list.data?.truncated ?? false}
                        error={list.error}
                        onRetry={() => {
                            void list.refetch();
                        }}
                        hasFilters={countActiveFilters(filters) > 0}
                        onClearFilters={() => {
                            applyFilters(EMPTY_FILTERS);
                        }}
                    />
                }
                detail={
                    <ProspectDetailPanel
                        row={openedRow}
                        product={product}
                        customer={customerDetail.data}
                        isCustomerLoading={isCustomerLoading}
                        credit={{
                            hasCustomer: openedCustomerId !== null,
                            facts: creditFacts,
                            rejections,
                        }}
                        wizard={{
                            session: activeSession.data,
                            // Con la ficha 360 en vuelo todavía no se sabe con qué fuente se lee.
                            isLoading: activeSession.isLoading || isCustomerLoading,
                            isError: activeSession.isError,
                            lookupEnabled: ACTIVE_SESSION_LOOKUP_ENABLED,
                            linked: readsLinkedSession
                                ? {
                                      sessionId: linkedSessionId,
                                      session: linkedSession.data,
                                      isLoading: linkedSession.isLoading,
                                      settled: settledProspect,
                                  }
                                : undefined,
                        }}
                        tab={tab}
                        onTabChange={setTab}
                        onReopen={() => {
                            void reopen();
                        }}
                        // La ficha 360 en vuelo puede traer el teléfono que un NEW necesita.
                        isReopening={resumeProspect.isPending || isCustomerLoading}
                        onOpenCustomer={openCustomer}
                        canOpenCustomer={canOpenCustomer}
                        onMarkLost={() => {
                            void markLostConfirmed();
                        }}
                        isMarkingLost={markLost.isPending}
                        confirmingLost={confirmingLost}
                        onConfirmingLostChange={setConfirmingLost}
                        onClose={closeDetail}
                    />
                }
            />
            {otherStorePhone !== null && (
                <CancelOtherStoreSaleDialog
                    phone={otherStorePhone}
                    open
                    onClose={() => {
                        setOtherStorePhone(null);
                    }}
                    onFreed={() => {
                        void reopen();
                    }}
                />
            )}
        </>
    );
}
